import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { suggestFieldMapping } from "@tilecast/widget-kit";
import { Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { api } from "../api/client";
import type {
  ContentDefinitionField,
  DataSource,
  DataSourceDefinition,
  DataSourceField,
} from "../api/types";
import { DateInput, DateTimeInput } from "../components/date-picker";
import { Button } from "../components/ui/button";
import { Field, FieldDescription, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  localDateTimeToRfc3339,
  rfc3339ToLocalDateTime,
} from "../lib/dateTime";
import { DataSourcePicker, type DataFormatGuide } from "./DataSourcePicker";

type Values = Record<string, unknown>;

function fieldText(value: unknown) {
  return typeof value === "string" || typeof value === "number"
    ? String(value)
    : "";
}

// resolveDataSourceKey returns which `data_source` control supplies the field list for a
// `data_source_field` control. An explicit `dataSourceKey` wins; otherwise a definition with
// exactly one Data Source control is unambiguous. When a definition declares several and the
// field does not say which, there is no correct answer, so no fields are offered rather than
// silently listing another source's schema. Nested repeating-group items carry no
// `data_source` sibling, so a keyless nested field falls back to the root's single source.
export function resolveDataSourceKey(
  field: ContentDefinitionField,
  fields: ContentDefinitionField[],
  rootFields?: ContentDefinitionField[],
): string | undefined {
  if (field.dataSourceKey) return field.dataSourceKey;
  const sourceFields = fields.filter(
    (candidate) => candidate.control === "data_source",
  );
  if (sourceFields.length === 1) return sourceFields[0]?.key;
  if (sourceFields.length === 0 && rootFields) {
    // Collect nested sources too: a group may itself declare the source.
    const collectNested = (
      list: ContentDefinitionField[],
    ): ContentDefinitionField[] => {
      const found: ContentDefinitionField[] = [];
      for (const candidate of list) {
        if (candidate.control === "data_source") found.push(candidate);
        if (candidate.itemFields?.length)
          found.push(...collectNested(candidate.itemFields));
      }
      return found;
    };
    const allRoot = collectNested(rootFields);
    if (allRoot.length === 1) return allRoot[0]?.key;
  }
  return undefined;
}

// dataSourceKeysIn lists every Data Source referenced by a configuration, so callers can
// resolve, preview, and validate all of them rather than assuming a single `dataSourceId`.
//
// Fields nest: a `repeating_group` carries `itemFields`, and a Data Source control may live inside
// one. Missing those would under-count the sources a Widget depends on, so preview and save gating
// would pass while data was still in flight.
export function dataSourceKeysIn(
  fields: ContentDefinitionField[],
  value: Values,
): string[] {
  const ids: string[] = [];
  for (const field of fields) {
    if (field.control === "data_source") {
      const id = fieldText(value[field.key]);
      if (id) ids.push(id);
      continue;
    }
    if (field.control !== "repeating_group" || !field.itemFields?.length)
      continue;
    const items = Array.isArray(value[field.key])
      ? (value[field.key] as Values[])
      : [];
    for (const item of items)
      ids.push(...dataSourceKeysIn(field.itemFields, item ?? {}));
  }
  return [...new Set(ids)];
}

export function DefinitionForm({
  fields,
  value,
  onChange,
  readOnly = false,
  csrf,
  rootValues,
  rootFields,
}: {
  fields: ContentDefinitionField[];
  value: Values;
  onChange: (value: Values) => void;
  readOnly?: boolean;
  csrf?: string;
  /**
   * The outermost configuration. Repeating-group items render a nested
   * form whose own values cannot name the Data Source, so field pickers
   * inside a group resolve against these root values instead.
   */
  rootValues?: Values;
  /** The outermost schema; nested keyless fields fall back to its single source. */
  rootFields?: ContentDefinitionField[];
}) {
  const root = rootValues ?? value;
  const rootSchema = rootFields ?? fields;
  const needsDataSources = fields.some(
    (field) =>
      field.control === "data_source" || field.control === "data_source_field",
  );
  const dataSources = useQuery({
    queryKey: ["definition-form-data-sources"],
    queryFn: () =>
      api.listDataSources(
        new URLSearchParams({ page: "1", pageSize: "100", sort: "name" }),
      ),
    enabled: needsDataSources,
  });
  const definitions = useQuery({
    queryKey: ["content-definitions"],
    queryFn: api.contentDefinitions,
  });
  const assets = useQuery({
    queryKey: ["definition-form-media-assets"],
    queryFn: () =>
      api.assets(
        new URLSearchParams({
          page: "1",
          pageSize: "100",
          status: "ready",
          sort: "name",
        }),
      ),
    enabled: fields.some((field) => field.control === "media_asset"),
  });
  const set = (key: string, next: unknown) =>
    onChange({ ...value, [key]: next });

  return (
    <div className="grid gap-4">
      {fields.map((field) => (
        <DefinitionControl
          key={field.key}
          field={field}
          fields={fields}
          values={value}
          rootValues={root}
          rootFields={rootSchema}
          value={value[field.key]}
          setValue={(next) => set(field.key, next)}
          readOnly={readOnly}
          csrf={csrf}
          dataSources={dataSources.data?.items ?? []}
          dataSourceDefinitions={definitions.data?.dataSources ?? []}
          assets={assets.data?.items ?? []}
        />
      ))}
    </div>
  );
}

// acceptsDefinition applies a `data_source` control's declared acceptance rules to a provider
// definition: the dataset kind it renders and any output fields it requires.
function acceptsDefinition(
  field: ContentDefinitionField,
  definition: DataSourceDefinition,
) {
  if (
    field.acceptedDataSourceKinds?.length &&
    !field.acceptedDataSourceKinds.includes(definition.outputSchema.kind)
  )
    return false;
  return Object.entries(field.requiredFields ?? {}).every(([key, type]) =>
    definition.outputSchema.fields.some(
      (output) => output.key === key && output.type === type,
    ),
  );
}

function compatibleSources(
  field: ContentDefinitionField,
  dataSources: DataSource[],
  dataSourceDefinitions: DataSourceDefinition[],
) {
  return dataSources.filter((source) => {
    const definition = dataSourceDefinitions.find(
      (candidate) => candidate.id === source.provider,
    );
    return definition ? acceptsDefinition(field, definition) : false;
  });
}

// creatableProviders narrows what the picker's Connect flow offers to providers this field would
// actually accept, so an author cannot create a Data Source the field then rejects.
function creatableProviders(
  field: ContentDefinitionField,
  dataSourceDefinitions: DataSourceDefinition[],
) {
  return dataSourceDefinitions
    .filter((definition) => acceptsDefinition(field, definition))
    .map((definition) => definition.id);
}

function exampleValue(key: string, type: string) {
  const normalized = key.toLowerCase();
  if (type === "datetime")
    return normalized.includes("end")
      ? "2026-08-24T09:51:00-04:00"
      : "2026-08-24T09:03:00-04:00";
  if (type === "date") return "2026-08-24";
  if (type === "number" || type === "currency") return 94.6;
  if (type === "integer") return 42;
  if (type === "percent") return 85;
  if (type === "boolean") return true;
  if (normalized.includes("title") || normalized.includes("name"))
    return "Period 2";
  if (normalized.includes("detail") || normalized.includes("description"))
    return "East wing";
  return "Example text";
}

function preferredExampleType(key: string, types: string[]) {
  const normalized = key.toLowerCase();
  if (
    (normalized.includes("date") ||
      normalized.includes("time") ||
      normalized.includes("start") ||
      normalized.includes("end")) &&
    types.includes("datetime")
  )
    return "datetime";
  return types[0] ?? "text";
}

type WidgetsT = TFunction<["content", "common"], undefined>;

// collectSelectableFields lists every data_source_field control that reads
// from the named source, including controls nested inside repeating groups.
// An explicit dataSourceKey wins; otherwise the level with exactly one Data
// Source control is unambiguous, mirroring resolveDataSourceKey. A keyless
// nested field with no local source falls back to the root single source.
function collectSelectableFields(
  fields: ContentDefinitionField[],
  sourceKey: string,
  rootFields: ContentDefinitionField[] = fields,
): ContentDefinitionField[] {
  const sourceFields = fields.filter(
    (candidate) => candidate.control === "data_source",
  );
  const rootSources: ContentDefinitionField[] = [];
  const collectSources = (list: ContentDefinitionField[]): void => {
    for (const candidate of list) {
      if (candidate.control === "data_source") rootSources.push(candidate);
      if (candidate.itemFields?.length) collectSources(candidate.itemFields);
    }
  };
  collectSources(rootFields);
  const rootSingle =
    rootSources.length === 1 && rootSources[0]?.key === sourceKey;
  const selectable: ContentDefinitionField[] = [];
  for (const candidate of fields) {
    if (
      candidate.control === "data_source_field" &&
      (candidate.dataSourceKey === sourceKey ||
        (!candidate.dataSourceKey &&
          (sourceFields.length === 1 ||
            (sourceFields.length === 0 && rootSingle))))
    ) {
      selectable.push(candidate);
    }
    if (
      candidate.control === "repeating_group" &&
      candidate.itemFields?.length
    ) {
      selectable.push(
        ...collectSelectableFields(candidate.itemFields, sourceKey, rootFields),
      );
    }
  }
  return selectable;
}

export function dataFormatGuideFor(
  sourceField: ContentDefinitionField,
  fields: ContentDefinitionField[],
  t?: WidgetsT,
): DataFormatGuide {
  const selectableFields = collectSelectableFields(fields, sourceField.key);
  const requirements: DataFormatGuide["fields"] = Object.entries(
    sourceField.requiredFields ?? {},
  ).map(([key, type]) => ({
    key,
    label: key.replaceAll("_", " "),
    types: [type],
    required: true,
  }));
  for (const field of selectableFields) {
    const types = field.dataSourceFieldTypes?.length
      ? field.dataSourceFieldTypes
      : ["text", "number", "date", "datetime"];
    requirements.push({
      key:
        typeof field.default === "string" && field.default
          ? field.default
          : field.key.replace(/Field$/, ""),
      label: field.label,
      types,
      required: field.required,
    });
  }
  // One entry per source key: a required field and a mapped control can describe
  // the same key under different labels, and the example below is keyed by key alone.
  const deduplicated: DataFormatGuide["fields"] = [];
  const byKey = new Map<string, DataFormatGuide["fields"][number]>();
  for (const field of requirements) {
    const merged = byKey.get(field.key);
    if (!merged) {
      const entry = { ...field, types: [...field.types] };
      byKey.set(field.key, entry);
      deduplicated.push(entry);
      continue;
    }
    for (const type of field.types)
      if (!merged.types.includes(type)) merged.types.push(type);
    merged.required = merged.required || field.required;
  }
  const kinds = sourceField.acceptedDataSourceKinds?.length
    ? sourceField.acceptedDataSourceKinds
    : ["records"];
  const shape = kinds
    .map((kind) =>
      kind === "records"
        ? (t?.("widgets.form.guide.shapeRecords") ?? "record rows")
        : kind === "object"
          ? (t?.("widgets.form.guide.shapeObject") ?? "a single object")
          : kind === "time_series"
            ? (t?.("widgets.form.guide.shapeTimeSeries") ?? "a time series")
            : kind.replaceAll("_", " "),
    )
    .join(t?.("widgets.form.guide.or") ?? " or ");
  const example = Object.fromEntries(
    deduplicated.map((field) => {
      const type = preferredExampleType(field.key, field.types);
      return [field.key, exampleValue(field.key, type)];
    }),
  );
  if (Object.keys(example).length === 0)
    example.title =
      t?.("widgets.form.guide.exampleTitle") ?? "Example information";
  return {
    shape: shape[0]!.toUpperCase() + shape.slice(1),
    summary:
      deduplicated.length > 0
        ? (t?.("widgets.form.guide.summaryMapped") ??
          "Use these field roles and types. Field names can differ because you map them below.")
        : (t?.("widgets.form.guide.summaryUnmapped") ??
          "Use one item per row; after connecting the source, choose which fields appear."),
    fields: deduplicated,
    example,
  };
}

function DefinitionControl({
  field,
  fields,
  values,
  rootValues,
  rootFields,
  value,
  setValue,
  readOnly,
  csrf,
  dataSources,
  dataSourceDefinitions,
  assets,
}: {
  field: ContentDefinitionField;
  fields: ContentDefinitionField[];
  values: Values;
  /** The outermost configuration; nested items resolve pickers against it. */
  rootValues: Values;
  rootFields: ContentDefinitionField[];
  value: unknown;
  setValue: (value: unknown) => void;
  readOnly: boolean;
  csrf?: string;
  dataSources: DataSource[];
  dataSourceDefinitions: DataSourceDefinition[];
  assets: { id: string; name: string; type: string }[];
}) {
  const { t } = useTranslation(["content", "common"]);
  // A field picker resolves against the source chosen by its own `data_source` control, not a
  // hardcoded `dataSourceId`, so a definition may reference several Data Sources.
  // Nested items pass their itemFields as `fields` with the root schema as
  // `rootFields`, so a keyless nested picker falls back to the root source.
  const fieldSourceKey =
    field.control === "data_source_field"
      ? resolveDataSourceKey(field, fields, rootFields)
      : undefined;
  const fieldSourceID = fieldSourceKey
    ? fieldText(values[fieldSourceKey]) || fieldText(rootValues[fieldSourceKey])
    : "";
  const fieldSource = useQuery({
    queryKey: ["definition-form-data-source", fieldSourceID],
    queryFn: () => api.getDataSource(fieldSourceID),
    enabled: Boolean(fieldSourceID),
  });
  // Automatic semantic mapping (§4.1): when a source is connected, an empty
  // field picker fills from declared roles, then legacy keys, then
  // compatible types. The author may override every mapping in the picker.
  const suggestedField = useMemo(() => {
    if (field.control !== "data_source_field") return "";
    const roles = field.ui?.semanticRole ? [field.ui.semanticRole] : [];
    const legacyKeys = field.ui?.legacyKeys ?? [];
    if (roles.length === 0 && legacyKeys.length === 0) return "";
    const sourceFields = fieldSource.data?.fields;
    if (!sourceFields) return "";
    return (
      suggestFieldMapping(sourceFields, {
        [field.key]: {
          roles,
          legacyKeys,
          types: field.dataSourceFieldTypes ?? [],
        },
      })[field.key] ?? ""
    );
  }, [field, fieldSource.data]);
  const lastSuggestedSource = useRef("");
  useEffect(() => {
    if (field.control !== "data_source_field" || readOnly) return;
    if (!fieldSourceID || !fieldSource.data) {
      lastSuggestedSource.current = "";
      return;
    }
    const current = fieldText(value);
    const freshSource = lastSuggestedSource.current !== fieldSourceID;
    lastSuggestedSource.current = fieldSourceID;
    const known = new Set(
      (fieldSource.data.fields ?? []).map((entry) => entry.key),
    );
    // An author choice for this source stands. A key the new source does
    // not have is stale from a previous source, so it remaps.
    if (current !== "" && (!freshSource || known.has(current))) return;
    if (suggestedField !== "" && suggestedField !== current)
      setValue(suggestedField);
  }, [
    field,
    fieldSourceID,
    fieldSource.data,
    suggestedField,
    value,
    readOnly,
    setValue,
  ]);
  const common = {
    disabled: readOnly,
    required: field.required,
  };
  const requiredMark = field.required ? " *" : "";
  const labelText = `${field.label}${requiredMark}`;
  if (field.control === "currency_code")
    return (
      <Field>
        <FieldLabel htmlFor={`definition-${field.key}`}>
          {t("dataSources.manual.currency")}
        </FieldLabel>
        <Input
          id={`definition-${field.key}`}
          {...common}
          value={fieldText(value).toUpperCase()}
          maxLength={3}
          autoCapitalize="characters"
          onChange={(event) => setValue(event.target.value.toUpperCase())}
        />
        <FieldDescription>
          {t("dataSources.manual.currencyHint")}
        </FieldDescription>
      </Field>
    );
  if (field.control === "data_source")
    return (
      <DataSourcePicker
        label={field.label}
        description={field.description}
        value={fieldText(value)}
        sources={compatibleSources(field, dataSources, dataSourceDefinitions)}
        createProviders={creatableProviders(field, dataSourceDefinitions)}
        formatGuide={dataFormatGuideFor(field, fields, t)}
        csrf={csrf}
        disabled={readOnly}
        required={field.required}
        onChange={setValue}
      />
    );
  if (field.control === "boolean")
    // The wrapping label names the switch; the span carries the label text
    // alone so the accessible name stays exact, with no extra aria-label.
    return (
      <Field>
        <FieldLabel className="flex items-center gap-2 text-sm">
          <Switch
            checked={!!value}
            disabled={readOnly}
            onCheckedChange={(checked) => setValue(checked === true)}
          />
          <span>{labelText}</span>
        </FieldLabel>
        {field.description && (
          <FieldDescription>{field.description}</FieldDescription>
        )}
      </Field>
    );
  if (field.control === "multiline_text")
    return (
      <Field>
        <FieldLabel htmlFor={`definition-${field.key}`}>{labelText}</FieldLabel>
        <Textarea
          id={`definition-${field.key}`}
          {...common}
          value={fieldText(value)}
          maxLength={field.maxLength}
          onChange={(event) => setValue(event.target.value)}
        />
        {field.description && (
          <FieldDescription>{field.description}</FieldDescription>
        )}
      </Field>
    );
  if (
    field.control === "select" ||
    field.control === "data_source_field" ||
    field.control === "media_asset"
  ) {
    const options =
      field.control === "select"
        ? (field.options ?? [])
        : field.control === "data_source_field"
          ? (fieldSource.data?.fields ?? [])
              .filter(
                (sourceField: DataSourceField) =>
                  !field.dataSourceFieldTypes?.length ||
                  field.dataSourceFieldTypes.includes(sourceField.type),
              )
              .map((sourceField: DataSourceField) => ({
                value: sourceField.key,
                label: `${sourceField.label} (${sourceField.type})`,
              }))
          : assets
              .filter(
                (asset) =>
                  !field.mediaTypes?.length ||
                  field.mediaTypes.includes(asset.type),
              )
              .map((asset) => ({ value: asset.id, label: asset.name }));
    const placeholder =
      field.control === "data_source_field" && !fieldSourceID
        ? t("widgets.form.selectSourceFirst")
        : t("widgets.form.selectPlaceholder");
    const labeledOptions = [{ value: "", label: placeholder }, ...options];
    return (
      <Field>
        <FieldLabel htmlFor={`definition-${field.key}`}>{labelText}</FieldLabel>
        <Select
          value={fieldText(value)}
          disabled={readOnly}
          required={field.required}
          onValueChange={(next) => setValue(next)}
          items={labeledOptions}
        >
          <SelectTrigger id={`definition-${field.key}`} aria-label={labelText}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {labeledOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {field.description && (
          <FieldDescription>{field.description}</FieldDescription>
        )}
      </Field>
    );
  }
  if (field.control === "repeating_group") {
    const items = Array.isArray(value) ? (value as Values[]) : [];
    return (
      <fieldset className="grid gap-3">
        <legend className="text-sm font-medium">{labelText}</legend>
        {items.map((item, index) => (
          <div className="grid gap-3 rounded-lg border p-3" key={index}>
            <DefinitionForm
              fields={field.itemFields ?? []}
              value={item}
              readOnly={readOnly}
              csrf={csrf}
              rootValues={rootValues}
              rootFields={rootFields}
              onChange={(next) =>
                setValue(
                  items.map((current, currentIndex) =>
                    currentIndex === index ? next : current,
                  ),
                )
              }
            />
            {!readOnly && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t("widgets.form.removeItem", {
                  label: field.label,
                  index: index + 1,
                })}
                onClick={() =>
                  setValue(items.filter((_, current) => current !== index))
                }
              >
                <Trash2 size={15} aria-hidden="true" />
              </Button>
            )}
          </div>
        ))}
        {!readOnly && items.length < (field.maximumItems ?? 0) && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setValue([...items, {}])}
          >
            <Plus size={15} aria-hidden="true" /> {t("widgets.form.addItem")}
          </Button>
        )}
      </fieldset>
    );
  }
  if (field.control === "date")
    return (
      <Field>
        <FieldLabel htmlFor={`definition-${field.key}`}>{labelText}</FieldLabel>
        <DateInput
          id={`definition-${field.key}`}
          {...common}
          aria-label={labelText}
          value={fieldText(value)}
          onChange={setValue}
        />
        {field.description && (
          <FieldDescription>{field.description}</FieldDescription>
        )}
      </Field>
    );
  if (field.control === "datetime")
    return (
      <Field>
        <FieldLabel htmlFor={`definition-${field.key}`}>{labelText}</FieldLabel>
        <DateTimeInput
          id={`definition-${field.key}`}
          {...common}
          aria-label={labelText}
          value={rfc3339ToLocalDateTime(fieldText(value))}
          onChange={(next) => setValue(localDateTimeToRfc3339(next))}
        />
        {field.description && (
          <FieldDescription>{field.description}</FieldDescription>
        )}
      </Field>
    );
  if (field.control === "local_datetime")
    // A wall-clock time that a sibling timezone field interprets: it is
    // shown and saved exactly as entered, never converted to an instant.
    // A saved RFC 3339 instant from an older release shows as its own
    // wall time until it is edited.
    return (
      <Field>
        <FieldLabel htmlFor={`definition-${field.key}`}>{labelText}</FieldLabel>
        <DateTimeInput
          id={`definition-${field.key}`}
          {...common}
          aria-label={labelText}
          value={fieldText(value).slice(0, 16)}
          onChange={setValue}
        />
        {field.description && (
          <FieldDescription>{field.description}</FieldDescription>
        )}
      </Field>
    );
  const inputType =
    field.control === "number" || field.control === "integer"
      ? "number"
      : field.control === "color"
        ? "color"
        : field.control === "url"
          ? "url"
          : "text";
  return (
    <Field>
      <FieldLabel htmlFor={`definition-${field.key}`}>{labelText}</FieldLabel>
      <Input
        id={`definition-${field.key}`}
        {...common}
        type={inputType}
        value={fieldText(value)}
        min={field.minimum}
        max={field.maximum}
        minLength={field.minLength}
        maxLength={field.maxLength}
        onChange={(event) =>
          setValue(
            field.control === "number" || field.control === "integer"
              ? event.target.value === ""
                ? undefined
                : Number(event.target.value)
              : event.target.value,
          )
        }
      />
      {field.description && (
        <FieldDescription>{field.description}</FieldDescription>
      )}
    </Field>
  );
}
